import React from "react";

import { api } from "src/common/utils/api";
import {
	useCreateGenericError,
	useCreateMessage
} from "src/features/notifications";
import { useOrgModParams } from "src/features/orgmod";
import { messages } from "src/views/invitation-list/messages";
import { Invitation, RawInvitation } from "src/views/invitation-list/types";
import { useNormalizeInvitation } from "src/views/invitation-list/useNormalizeInvitation";

type InvitationListAction =
	| FetchStartAction
	| FetchSuccessAction
	| FetchFailureAction
	| ResendStartAction
	| ResendSuccessAction
	| ResendFailureAction
	| DeleteStartAction
	| DeleteSuccessAction
	| DeleteFailureAction;

interface FetchStartAction {
	type: "FETCH_START";
}

interface FetchSuccessAction {
	type: "FETCH_SUCCEESS";
	payload: { invitations: Invitation[] };
}

interface FetchFailureAction {
	type: "FETCH_FAILURE";
	payload: { error: unknown };
}

interface ResendStartAction {
	type: "RESEND_START";
	payload: { token: string };
}

interface ResendSuccessAction {
	type: "RESEND_SUCCESS";
	payload: { updatedInvitation: Invitation };
}

interface ResendFailureAction {
	type: "RESEND_FAILURE";
	payload: { token: string; error: unknown };
}

interface DeleteStartAction {
	type: "DELETE_START";
	payload: { token: string };
}

interface DeleteSuccessAction {
	type: "DELETE_SUCCESS";
	payload: { token: string };
}

interface DeleteFailureAction {
	type: "DELETE_FAILURE";
	payload: { token: string; error: unknown };
}

interface InvitationListState {
	isLoading: boolean;
	error: unknown | null;
	invitations: InvitationItemState[];
}

interface InvitationItemState {
	isLoading: boolean;
	error: unknown | null;
	token: string;
	name: string;
	email: string;
	role: string;
}

const initialState: InvitationListState = {
	isLoading: false,
	error: null,
	invitations: []
};

export function useInvitationList(): InvitationListState & {
	resendInvitation(token: string): void;
	deleteInvitation(token: string): void;
} {
	const [state, dispatch] = React.useReducer(reducer, initialState);

	const normalize = useNormalizeInvitation();
	const createMessage = useCreateMessage();
	const createGenericError = useCreateGenericError();

	const { org, mod } = useOrgModParams();

	const fetchInvitations = React.useCallback(async () => {
		dispatch({ type: "FETCH_START" });
		try {
			const { data } = await api.get<RawInvitation[]>(
				`/invites?organization=${org}&module=${mod}`
			);
			dispatch({
				type: "FETCH_SUCCEESS",
				payload: { invitations: data.map(normalize) }
			});
		} catch (error) {
			dispatch({ type: "FETCH_FAILURE", payload: { error } });
			createGenericError();
		}
	}, [normalize, createGenericError, org, mod]);

	React.useEffect(() => {
		fetchInvitations();
	}, [fetchInvitations]);

	const resendInvitation = React.useCallback(
		async (token: string) => {
			dispatch({ type: "RESEND_START", payload: { token } });
			try {
				const { data } = await api.put(
					`/invites/${token}?organization=${org}&module=${mod}`
				);
				dispatch({
					type: "RESEND_SUCCESS",
					payload: { updatedInvitation: normalize(data) }
				});
				createMessage(messages.resendSuccess);
			} catch (error) {
				dispatch({ type: "RESEND_FAILURE", payload: { token, error } });
				createGenericError();
			}
		},
		[normalize, createMessage, createGenericError, org, mod]
	);

	const deleteInvitation = React.useCallback(
		async (token: string) => {
			dispatch({ type: "DELETE_START", payload: { token } });
			try {
				await api.delete(
					`/invites/${token}?organization=${org}&module=${mod}`
				);
				dispatch({ type: "DELETE_SUCCESS", payload: { token } });
				createMessage(messages.deleteSuccess);
			} catch (error) {
				dispatch({ type: "DELETE_FAILURE", payload: { token, error } });
				createGenericError();
			}
		},
		[createMessage, createGenericError, org, mod]
	);

	return { ...state, resendInvitation, deleteInvitation };
}

function reducer(
	state: InvitationListState,
	action: InvitationListAction
): InvitationListState {
	if (action.type === "FETCH_START") {
		return handleFetchStart(state);
	}
	if (action.type === "FETCH_SUCCEESS") {
		return handleFetchSuccess(state, action);
	}
	if (action.type === "FETCH_FAILURE") {
		return handleFetchFailure(state, action);
	}
	if (action.type === "RESEND_START" || action.type === "DELETE_START") {
		return handleItemStart(state, action);
	}
	if (action.type === "RESEND_SUCCESS") {
		return handleResendSuccess(state, action);
	}
	if (action.type === "DELETE_SUCCESS") {
		return handleDeleteSuccess(state, action);
	}
	if (action.type === "RESEND_FAILURE" || action.type === "DELETE_FAILURE") {
		return handleItemFailure(state, action);
	}
	return state;
}

function handleFetchStart(state: InvitationListState): InvitationListState {
	return { ...state, isLoading: true, error: null, invitations: [] };
}

function handleFetchSuccess(
	state: InvitationListState,
	{ payload: { invitations } }: FetchSuccessAction
): InvitationListState {
	return {
		...state,
		isLoading: false,
		invitations: invitations.map(invitation => ({
			...invitation,
			isLoading: false,
			error: null
		}))
	};
}

function handleFetchFailure(
	state: InvitationListState,
	{ payload: { error } }: FetchFailureAction
): InvitationListState {
	return { ...state, isLoading: false, error };
}

function handleItemStart(
	state: InvitationListState,
	{ payload: { token } }: ResendStartAction | DeleteStartAction
): InvitationListState {
	return {
		...state,
		invitations: state.invitations.map(invitation =>
			invitation.token === token
				? { ...invitation, isLoading: true }
				: invitation
		)
	};
}

function handleResendSuccess(
	state: InvitationListState,
	{ payload: { updatedInvitation } }: ResendSuccessAction
): InvitationListState {
	return {
		...state,
		invitations: state.invitations.map(invitation =>
			invitation.token === updatedInvitation.token
				? { ...updatedInvitation, isLoading: false, error: null }
				: invitation
		)
	};
}

function handleDeleteSuccess(
	state: InvitationListState,
	{ payload: { token } }: DeleteSuccessAction
): InvitationListState {
	return {
		...state,
		invitations: state.invitations.filter(
			invitation => invitation.token !== token
		)
	};
}

function handleItemFailure(
	state: InvitationListState,
	{ payload: { token, error } }: ResendFailureAction | DeleteFailureAction
): InvitationListState {
	return {
		...state,
		invitations: state.invitations.map(invitation =>
			invitation.token === token
				? { ...invitation, isLoading: false, error }
				: invitation
		)
	};
}
